import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import moment from 'moment-timezone'
import { useTheme } from 'styled-components/native'

import {
  Message,
  MessageConversation,
  Provider,
  PROVIDER_LYRA_TYPES,
  ProviderInfo,
} from '@lyrahealth-inc/shared-app-logic'
import { aiCoachProviderInfo, ThemeType } from '@lyrahealth-inc/ui-core-crossplatform'

import { useShouldShowAICoach } from '../common/hooks/useShouldShowAICoach'
import {
  getAICoachConversations,
  getConversations,
  getMessengerDrafts,
  getProviders,
  getSelectedProviderId,
} from '../data/lyraTherapy/lyraTherapySelectors'

export interface UseGetSelectedProviderConversationReturn {
  conversations: MessageConversation[]
  providers: Provider[]
  selectedConversation: MessageConversation
  selectedConversationId: string
  selectedProvider?: ProviderInfo
  selectedDraft: Message[]
  isAICoachSelected: boolean
}

type ProviderWithTimestamp = {
  latestMessageDatetime: string | null
} & Provider

/**
 * Hook that encapsulates the logic for managing selected provider and conversation state.
 * This hook provides utilities for getting the currently selected provider and conversation.
 */
export const useGetSelectedProviderConversation = (): UseGetSelectedProviderConversationReturn => {
  const { breakpoints } = useTheme() as ThemeType
  const { showAiCoach } = useShouldShowAICoach()
  const selectedMessengerProviderId = useSelector(getSelectedProviderId)
  const conversations = useSelector(getConversations)?.toJS() || []
  const providersList = useSelector(getProviders)?.toJS() || []
  const drafts = useSelector(getMessengerDrafts)?.toJS() || []
  const aiConversations = useSelector(getAICoachConversations)?.toJS() || []

  const aiCoach = {
    provider: { ...aiCoachProviderInfo, lyra_id: aiConversations[0]?.provider_lyra_id },
    appointments: [],
  }

  const conversationProviderIds = new Set(
    conversations.map((conversation: MessageConversation) => conversation?.provider_lyra_id),
  )

  const providerIdToProvider = {}
  providersList.forEach((provider: Provider) => {
    if (conversationProviderIds.has(provider?.provider?.lyra_id)) {
      providerIdToProvider[provider?.provider?.lyra_id] = provider
    }
  })

  const filteredProviderList: ProviderWithTimestamp[] = conversations
    .map((conversation: MessageConversation) => {
      if (providerIdToProvider[conversation?.provider_lyra_id]) {
        const latestMessageDatetime = conversation?.conversation_last_message_sent_datetime
        return { ...providerIdToProvider[conversation?.provider_lyra_id], latestMessageDatetime }
      } else {
        return null
      }
    })
    .filter((providerWithLatestMessage: ProviderWithTimestamp | null) => providerWithLatestMessage !== null)

  const sortedProviders: Provider[] = useMemo(() => {
    return breakpoints.isMobileSized
      ? filteredProviderList.sort((a, b) =>
          a.latestMessageDatetime && b.latestMessageDatetime
            ? moment(a.latestMessageDatetime).isBefore(b.latestMessageDatetime)
              ? -1
              : 1
            : 0,
        )
      : filteredProviderList.sort((a, b) =>
          a.latestMessageDatetime && b.latestMessageDatetime
            ? moment(b.latestMessageDatetime).isBefore(a.latestMessageDatetime)
              ? -1
              : 1
            : 0,
        )
  }, [breakpoints.isMobileSized, filteredProviderList])

  const providers = showAiCoach ? ([aiCoach, ...sortedProviders] as Provider[]) : sortedProviders
  const selectedProvider = providers.find(
    (provider: Provider) => provider.provider.lyra_id === selectedMessengerProviderId,
  )?.provider
  const selectedConversation = conversations.find(
    (conversation: MessageConversation) => conversation.provider_lyra_id === selectedMessengerProviderId,
  )
  const selectedConversationId = selectedConversation?.conversation_id
  const selectedDraft = drafts.find((draft: Dict) => draft.conversationId === selectedConversationId)?.content || ''
  const isAICoachSelected = selectedProvider?.lyra_type === PROVIDER_LYRA_TYPES.AI_COACH

  return {
    conversations,
    providers,
    selectedConversation,
    selectedConversationId,
    selectedProvider,
    selectedDraft,
    isAICoachSelected,
  }
}
